import { useSyncExternalStore } from 'react';

/** Height the on-screen keyboard takes at the very least, on any phone. */
const KEYBOARD_MIN_PX = 150;

const subscribeViewport = (onChange: () => void) => {
  const vv = window.visualViewport;
  if (!vv) return () => {};
  vv.addEventListener('resize', onChange);
  return () => vv.removeEventListener('resize', onChange);
};

/**
 * Whether the on-screen keyboard is up.
 *
 * Phones shrink the VISUAL viewport for the keyboard and leave the layout
 * viewport alone, so a fixed tab bar either hides behind the keyboard or, on
 * iOS, floats over the field being typed into. Native apps hide the tab bar
 * while typing; the shell does the same with this.
 *
 * Pinch-zoom shrinks the visual viewport too, which is why the height is
 * scaled back up before comparing: zoom alone leaves no gap.
 */
export function useKeyboardOpen(): boolean {
  return useSyncExternalStore(
    subscribeViewport,
    () => {
      const vv = window.visualViewport;
      if (!vv) return false;
      return window.innerHeight - vv.height * vv.scale > KEYBOARD_MIN_PX;
    },
    () => false,
  );
}

const subscribeOnline = (onChange: () => void) => {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
};

/** False while the device reports no network. */
export function useOnline(): boolean {
  return useSyncExternalStore(
    subscribeOnline,
    () => navigator.onLine,
    () => true,
  );
}
